import React from "react";
import { PlaygroundChartRenderer } from "./PlaygroundChartRenderer";
import { PlaygroundType, type ExposureTimeseriesMetadata } from "@/services/api/widget/types";
import TimePeriodSelector, { type Period } from "@/components/TimePeriodSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faGrid2Plus } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { Toolbar } from "@/components/ui/toolbar/Toolbar";
import { Button } from "@/components/ui/button/Button";
import type { ExtendedAssetInfo } from "../hooks/useAssetManager";
import type { ChartSettings } from "../hooks/usePlaygroundSettings";
import type { VisualSettings } from "../hooks/useVisualSettings";
import type { ExposureBandsMap } from "../adapters/echarts/types";
import { LoadingIndicator } from "@/components/chat";
import {
  handleChartExport,
  type ExportContext,
} from "../utils/exportChartData";
import type { ExportFormat } from "../utils/downloadCSV";

interface ChartAreaProps {
  playgroundType: PlaygroundType;
  selectedAssets: string[];
  assetDisplayNames: Record<string, string>;
  assetMetadata: Record<string, ExtendedAssetInfo>;
  assetColors: Record<string, string>;
  chartSettings: Partial<ChartSettings>;
  visualSettings: VisualSettings;
  exposureBands?: ExposureBandsMap | null;
  periods: Period[];
  data: any;
  assetStats: any;
  isLoading: boolean;
  isInitialized?: boolean; // Whether URL params and initial state have been loaded
  onPeriodChange: (period: string) => void;
  onCustomDateChange: (range: [string, string]) => void;
  onOpenVisualSettings: () => void;
  onAddToDashboard?: () => void;
}

function formatMissingDate(dateValue: string): string {
  const date = new Date(`${dateValue}T00:00:00`);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export const ChartArea: React.FC<ChartAreaProps> = ({
  playgroundType,
  selectedAssets,
  assetDisplayNames,
  assetMetadata,
  assetColors,
  chartSettings,
  visualSettings,
  exposureBands = null,
  periods,
  data,
  assetStats,
  isLoading,
  isInitialized = false,
  onPeriodChange,
  onCustomDateChange,
  onOpenVisualSettings,
  onAddToDashboard,
}) => {
  const exposureMetadata = assetStats?.metadata as
    | ExposureTimeseriesMetadata
    | undefined;
  const missingDates = exposureMetadata?.missing_dates ?? [];
  const proxyExtensions =
    (assetStats?.metadata as { proxy_extensions?: Record<string, unknown> } | undefined)
      ?.proxy_extensions ?? {};
  const hasProxyExtensions = Object.keys(proxyExtensions).length > 0;
  const isExposureOverTimeSingleResourceInvalid =
    playgroundType === PlaygroundType.ExposureOverTime &&
    selectedAssets.length !== 1;

  // Handle export using the utility function
  const handleExport = (format: ExportFormat) => {
    const context: ExportContext = {
      selectedAssets,
      assetDisplayNames,
      assetStats,
      chartSettings: {
        displayMode: chartSettings.displayMode,
        xLabel: chartSettings.xLabel,
        yLabel: chartSettings.yLabel,
      },
    };

    handleChartExport(playgroundType, data, context, format);
  };

  return (
    <div className="w-full rounded-lg flex flex-col h-full space-y-4">
      {/* Toolbar with time period selector and action buttons */}
      <Toolbar>
        <div className="flex items-center space-x-4">
          {(playgroundType === PlaygroundType.PerformanceHistory ||
            playgroundType === PlaygroundType.RollingStats ||
            playgroundType === PlaygroundType.MetricsOverTime ||
            playgroundType === PlaygroundType.ExposureOverTime) && (
            <TimePeriodSelector
              period={chartSettings.period || "3y"}
              setPeriod={onPeriodChange}
              periods={periods}
              customDateRange={chartSettings.customDateRange || ["", ""]}
              setCustomDateRange={onCustomDateChange}
            />
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            color="color"
            className="px-2 py-1 min-h-0 border-none shadow-none"
            onClick={() => handleExport("csv")}
            title="Export chart data"
            dropdown={true}
            dropdownItems={[
              {
                name: "Export CSV",
                onClick: () => handleExport("csv"),
              },
              {
                name: "Export TSV",
                onClick: () => handleExport("tsv"),
              },
            ]}
          >
            Export
          </Button>
          {onAddToDashboard && (
            <Button
              color="color"
              className="px-2 py-1 min-h-0 border-none shadow-none"
              onClick={onAddToDashboard}
              title="Add to dashboard"
            >
              <FontAwesomeIcon icon={faGrid2Plus} className="h-4 w-4" />
            </Button>
          )}
          <Button
            color="color"
            className="px-2 py-1 min-h-0 border-none shadow-none"
            onClick={onOpenVisualSettings}
            title="Open visual settings"
          >
            <FontAwesomeIcon icon={faGear} className="h-4 w-4" />
          </Button>
        </div>
      </Toolbar>

      {/* Chart Display Area */}
      <div className="flex-grow h-full relative p-4">
        {!isInitialized || isLoading ? (
          // Show loading when: not initialized yet OR actively loading
          <div className="w-full h-full flex items-center justify-center rounded-lg">
            <div className="text-center p-6 space-y-12">
              <div className="flex items-center justify-center">
                <LoadingIndicator />
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-mono">
                Loading chart data
              </p>
            </div>
          </div>
        ) : selectedAssets.length === 0 ? (
          // Show empty state when initialized and no assets selected
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center p-6">
              <div className="text-lg font-medium mb-2 text-slate-900 dark:text-white">
                No resources selected
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                Use the search above to add resources, or create a new list to get started.
              </p>
            </div>
          </div>
        ) : isExposureOverTimeSingleResourceInvalid ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center p-6">
              <div className="text-lg font-medium mb-2 text-slate-900 dark:text-white">
                Single resource required
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                Exposure (Over time) supports a single resource. Please select
                exactly one portfolio, product, or benchmark.
              </p>
            </div>
          </div>
        ) : data === null || data === undefined ? (
          // Show loading for data fetch (assets selected, waiting for data)
          <div className="w-full h-full flex items-center justify-center rounded-lg">
            <div className="text-center p-6 space-y-12">
              <div className="flex items-center justify-center">
                <LoadingIndicator />
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-mono">
                Loading chart data
              </p>
            </div>
          </div>
        ) : Array.isArray(data) && data.length === 0 ? (
          // Only show "Insufficient Data" when fetch completed but returned empty ([])
          <div className="w-full h-full flex items-center justify-center rounded-lg">
            <div className="text-center p-6">
              <div className="text-lg font-medium mb-2 text-slate-900 dark:text-white">
                Insufficient Data
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {chartSettings.statistic === "beta" ||
                chartSettings.statistic === "correlation"
                  ? "No data available. Ensure each resource has a primary benchmark configured."
                  : "No data available for the selected assets and settings."}
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col">
            {missingDates.length > 0 && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                No snapshot data for:{" "}
                {missingDates.map(formatMissingDate).join("; ")}
              </div>
            )}
            {hasProxyExtensions &&
              (playgroundType === PlaygroundType.PerformanceHistory ||
                playgroundType === PlaygroundType.RollingStats ||
                playgroundType === PlaygroundType.Underwater) && (
              <div className="mb-3 rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-200">
                Dashed segments show proxy-extended performance at window edges.
                Interior gaps remain unfilled.
              </div>
            )}
            <div className="flex-1 min-h-0">
              <PlaygroundChartRenderer
                className="min-h-96 h-full"
                playgroundType={playgroundType}
                rawData={data}
                assetStats={assetStats}
                selectedAssets={selectedAssets}
                assetDisplayNames={assetDisplayNames}
                assetMetadata={
                  assetMetadata as unknown as Record<string, Record<string, unknown>>
                }
                assetColors={assetColors}
                chartSettings={{
                  displayMode: chartSettings.displayMode as
                    | "index"
                    | "performance"
                    | undefined,
                  rebaseValue: chartSettings.rebaseValue,
                  statistic: chartSettings.statistic,
                  windowSize: chartSettings.windowSize,
                  exposureType: chartSettings.exposureType,
                  combineVariants: chartSettings.combineVariants,
                  exposureDisplayMode: chartSettings.exposureDisplayMode,
                  xStatistic: chartSettings.xStatistic,
                  yStatistic: chartSettings.yStatistic,
                  xLabel: chartSettings.xLabel,
                  yLabel: chartSettings.yLabel,
                  legendMode: chartSettings.legendMode,
                  legendPosition: chartSettings.legendPosition,
                }}
                exposureBands={exposureBands}
                isLoading={isLoading}
                smoothLines={visualSettings.smoothLines}
                showAreaGradient={visualSettings.showAreaGradient}
                showGridColumns={visualSettings.showGridColumns}
                showGridRows={visualSettings.showGridRows}
                showCrosshair={visualSettings.showCrosshair}
                showPatternBackground={visualSettings.showPatternBackground}
                showXAxis={visualSettings.showXAxis}
                showYAxis={visualSettings.showYAxis}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
